import { useCallback, useMemo } from 'react';

import { useTrackAnnouncementActionClicked, useTrackAnnouncementDismissed } from '@air/analytics';
import { type IconComponent } from '@air/design-icons';
import {
  ANNOUNCEMENT_COMMAND_SURFACES,
  type AnnouncementAction,
  type AnnouncementCommand,
  type AnnouncementCommandAction,
  type AnnouncementMediaSource,
  type AnnouncementPlacement,
  type AnnouncementSurface,
  type AnnouncementTone,
  filterAnnouncementActionsBySupportedCommands,
  isAnnouncementCommandAction,
  isAnnouncementMediaSurface,
  narrowAnnouncementSettings,
  resolveAnnouncementTone,
} from '@air/feature-announcements';

import { getAnnouncementMediaForApp } from '~/components/announcements/getAnnouncementMediaForApp';
import { openAnnouncementLink } from '~/components/announcements/openAnnouncementLink';
import { useActiveAnnouncement } from '~/components/announcements/useActiveAnnouncement';
import { useAnnouncementIcon } from '~/components/announcements/useAnnouncementIcon';
import { usePrivateAnnouncementDismissMutation } from '~/components/v2/hooks/queries/mutations/usePrivateAnnouncementDismissMutation';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type UseAnnouncementForSurfaceParams = {
  /** Hides a supported command action when this returns false. Omitted means every supported command shows. */
  isCommandActionAvailable?: (action: AnnouncementCommandAction) => boolean;
  onCommand?: (command: AnnouncementCommand, action: AnnouncementCommandAction, announcementId: string) => void;
  supportedCommands?: readonly AnnouncementCommand[];
  surface: AnnouncementSurface;
};

export type AnnouncementForSurface = {
  /** Only actions this mount can run; command actions the surface can't run or without a matching `supportedCommands` entry are dropped. */
  actions: readonly AnnouncementAction[];
  body: string | null;
  icon: IconComponent | undefined;
  id: string;
  media: AnnouncementMediaSource | null;
  onAction: (action: AnnouncementAction) => void;
  onDismiss: () => void;
  placement: AnnouncementPlacement;
  title: string;
  tone: AnnouncementTone;
};

/** Everything a surface needs to show its announcement, leaving the markup to the surface. Null when nothing should show. */
export const useAnnouncementForSurface = ({
  isCommandActionAvailable,
  onCommand,
  supportedCommands,
  surface,
}: UseAnnouncementForSurfaceParams): AnnouncementForSurface | null => {
  const { announcementsEnabled } = useTemporaryFeatureFlags();
  const { currentWorkspace } = useCurrentWorkspace();
  const announcement = useActiveAnnouncement({ enabled: announcementsEnabled, surface });
  const { mutate: dismissAnnouncement } = usePrivateAnnouncementDismissMutation();
  const { trackAnnouncementActionClicked } = useTrackAnnouncementActionClicked();
  const { trackAnnouncementDismissed } = useTrackAnnouncementDismissed();

  const presentation = useMemo(() => {
    if (!announcement) {
      return null;
    }

    const settings = narrowAnnouncementSettings(announcement);
    const placement = settings.placements[surface];

    if (!placement) {
      return null;
    }

    const tone = resolveAnnouncementTone({ placement, style: settings.style });
    const surfaceCommands = supportedCommands?.filter((command) =>
      ANNOUNCEMENT_COMMAND_SURFACES[command].includes(surface),
    );
    const actions = filterAnnouncementActionsBySupportedCommands(settings.actions, surfaceCommands).filter(
      (action) => !isAnnouncementCommandAction(action) || (isCommandActionAvailable?.(action) ?? true),
    );

    return {
      actions,
      body: announcement.body,
      icon: settings.style.icon,
      media: isAnnouncementMediaSurface(surface) ? getAnnouncementMediaForApp(settings.media) : null,
      placement,
      tone,
    };
  }, [announcement, isCommandActionAvailable, supportedCommands, surface]);

  const dismissSilently = useCallback(() => {
    if (!announcement || !currentWorkspace?.id) {
      return;
    }

    dismissAnnouncement({
      announcementId: announcement.id,
      workspaceId: currentWorkspace.id,
    });
  }, [announcement, currentWorkspace?.id, dismissAnnouncement]);

  const onDismiss = useCallback(() => {
    if (!announcement) {
      return;
    }

    trackAnnouncementDismissed({
      announcement_id: announcement.id,
      name: announcement.name,
      surface,
    });

    dismissSilently();
  }, [announcement, dismissSilently, surface, trackAnnouncementDismissed]);

  const onAction = useCallback(
    (action: AnnouncementAction) => {
      if (!announcement) {
        return;
      }

      trackAnnouncementActionClicked({
        action_label: action.label,
        action_type: action.type,
        announcement_id: announcement.id,
        name: announcement.name,
        surface,
      });

      if (isAnnouncementCommandAction(action)) {
        onCommand?.(action.command, action, announcement.id);
      } else {
        openAnnouncementLink(action.url);
      }
    },
    [announcement, onCommand, surface, trackAnnouncementActionClicked],
  );

  const icon = useAnnouncementIcon(presentation?.icon);

  return useMemo(() => {
    if (!announcementsEnabled || !announcement || !presentation) {
      return null;
    }

    return {
      actions: presentation.actions,
      body: presentation.body,
      icon: icon ?? undefined,
      id: announcement.id,
      media: presentation.media,
      onAction,
      onDismiss,
      placement: presentation.placement,
      title: announcement.title,
      tone: presentation.tone,
    };
  }, [announcement, announcementsEnabled, icon, onAction, onDismiss, presentation]);
};
